import {
  Box,
  CircularProgress,
  Container,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { GetApp as GetAppIcon } from '@material-ui/icons';
import React, { memo, useState } from 'react';
import Loader from 'src/components/Loader/Loader';
import useAuth from 'src/hooks/useAuth';
import useFeatureFlags from 'src/hooks/useFeatureFlags';
import GoBackButton from '../../GoBackButton/GoBackButton';
import NotFoundPage from '../NotFoundPage';
import { useBookingsStore } from './bookingsStore';
import BookingsTable from './BookingsTable';
import { useProfessionalDevelopmentApprovalsStyles } from './styles';
import useBookings from './useBookings';
import { exportBookings } from './utils';

const ProfessionalDevelopmentApprovalsPage: React.FC = () => {
  const { isEnabled, haveFlagsLoaded } = useFeatureFlags();
  const { user } = useAuth();
  const classes = useProfessionalDevelopmentApprovalsStyles();
  const [tabValue, setTabValue] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const { isLoading } = useBookings(
    haveFlagsLoaded && isEnabled('mycambridge.ProfessionalDevelopmentApprovals')
      ? user?.organisationLegacyNumber
      : undefined
  );

  const pendingCount = useBookingsStore((state) => state.pending.length);
  const approvedCount = useBookingsStore((state) => state.approved.length);
  const rejectedCount = useBookingsStore((state) => state.rejected.length);
  const isChildOrganisation = useBookingsStore(
    (state) => state.isChildOrganisation
  );

  const handleTabChange = (
    _event: React.ChangeEvent<any>,
    newValue: number
  ) => {
    setTabValue(newValue);
  };

  const hasData = pendingCount > 0 || approvedCount > 0 || rejectedCount > 0;

  const emptyStateMessage =
    isChildOrganisation === true
      ? 'Bookings for this organisation are managed by the parent organisation. Please switch to the parent organisation or contact your administrator for support.'
      : isChildOrganisation === false
      ? 'This organisation does not have any bookings. Please contact your administrator for support.'
      : 'This organisation does not have any bookings or this may be a child organisation. Please contact your administrator for support.';

  const noResultsMessage = (
    <Box display="flex" justifyContent="center" textAlign="center">
      <Typography variant="body1">{emptyStateMessage}</Typography>
    </Box>
  );

  const handleExport = async () => {
    const centreNumber =
      user?.organisationLegacyNumber ?? user?.organisationId ?? '';

    setIsExporting(true);
    try {
      await exportBookings(centreNumber);
    } finally {
      setIsExporting(false);
    }
  };

  if (!haveFlagsLoaded || isLoading) {
    return <Loader />;
  }

  if (!isEnabled('mycambridge.ProfessionalDevelopmentApprovals')) {
    return (
      <div data-testid="not-found-page">
        <NotFoundPage />
      </div>
    );
  }

  return (
    <Container disableGutters maxWidth="xl">
      <Box mb={2}>
        <GoBackButton defaultPath="/" />
      </Box>

      <Box mb={4}>
        <Typography variant="h2">
          Professional Development Course Approvals
        </Typography>
      </Box>

      {hasData && (
        <Box className={classes.downloadButtonContainer}>
          <IconButton
            className={classes.downloadButton}
            disabled={isExporting}
            onClick={handleExport}
            title="Download all booking data"
            data-testid="download-button"
          >
            {isExporting ? (
              <CircularProgress size={24} data-testid="download-loading" />
            ) : (
              <GetAppIcon />
            )}
          </IconButton>
        </Box>
      )}

      <Box mb={3}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="course approval tabs"
          className={classes.tabs}
        >
          <Tab
            label="Pending"
            id="tab-0"
            aria-controls="tabpanel-0"
            data-testid="tab-pending"
          />
          <Tab
            label="Approved"
            id="tab-1"
            aria-controls="tabpanel-1"
            data-testid="tab-approved"
          />
          {isEnabled(
            'mycambridge.ProfessionalDevelopmentApprovals.rejectedTable'
          ) && (
            <Tab
              label="Rejected"
              id="tab-2"
              aria-controls="tabpanel-2"
              data-testid="tab-rejected"
            />
          )}
        </Tabs>
      </Box>

      <BookingsTable value={tabValue} noResultsMessage={noResultsMessage} />
    </Container>
  );
};

export default memo(ProfessionalDevelopmentApprovalsPage);
